import { Autocomplete, Box, Button, IconButton, TextField, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { adminTeamsService } from '../../../services/admin/teams'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { fieldTemplateService } from '../../../services/fieldTemplateService'
import DeleteIcon from '@mui/icons-material/Delete'
import { toast } from 'react-toastify'

const toTitleCase = (str) =>
  str
    .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
    .map((x) => x.slice(0, 1).toUpperCase() + x.slice(1))
    .join(' ')

const VISIBILITY_CONSTRAINT_TYPE = [
  { value: 'team', label: 'Team' },
  { value: 'team_type', label: 'Team Type' },
]

const FieldVisibilityProtectionUI = ({ field }) => {
  const [teams, setTeams] = useState([])
  const [teamTypes, setTeamTypes] = useState([])
  const [constraints, setConstraints] = useState([])
  const { user } = useAuthContext()

  useEffect(() => {
    ;(async () => {
      const teamList = await adminTeamsService.getTeams(user)
      setTeams(teamList)

      const teamTypesData = teamList.reduce((acc, cur) => {
        if (acc.includes(cur.team_type)) {
          return acc
        } else {
          return [...acc, cur.team_type]
        }
      }, [])

      setTeamTypes(teamTypesData.map((it) => ({ value: it, label: toTitleCase(it) })))

      const { teams: teamConstraints, teamTypes: teamTypeConstraints } =
        await fieldTemplateService.getFieldProtectionConstraints(user, field.id)

      let initConstraints = []

      teamConstraints.forEach((team) => {
        initConstraints.push({
          field_template_id: team.field_template_id,
          basis: 'team',
          basis_id: team.team_id,
        })
      })

      teamTypeConstraints.forEach((teamType) => {
        initConstraints.push({
          tempId: new Date().getTime(),
          field_template_id: teamType.field_template_id,
          basis: 'team_type',
          basis_id: teamType.team_type,
        })
      })

      setConstraints(initConstraints)
    })()
  }, [])

  const getSelectedTeam = (basis_id) => {
    const team = teams.find((it) => it.id == basis_id)
    if (team) {
      return { value: team.id, label: team.name }
    } else {
      return null
    }
  }

  const addRow = () => {
    setConstraints([
      ...constraints,
      {
        tempId: new Date().getTime(),
        field_template_id: null,
        basis: 'team_type',
        basis_id: '',
      },
    ])
  }

  const onDelete = (tempId) => {
    setConstraints(constraints.filter((it) => it.tempId != tempId))
  }

  const saveChanges = async () => {
    try {
      await fieldTemplateService.addFieldProtectionConstraints(user, field.id, constraints)
      toast.success('Added successfully')
    } catch (error) {
      toast.error('Error while adding conditional enabling/disabling')
    }
  }

  return (
    <Box>
      <Typography variant="p">Apply Visibility Protection</Typography>
      <br />
      {constraints.map((constraint) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', marginTop: 16, columnGap: 16 }}>
            <Autocomplete
              value={{
                value: constraint.basis,
                label: VISIBILITY_CONSTRAINT_TYPE.find((it) => it.value == constraint.basis)?.label,
              }}
              id="autocomplete-field"
              options={VISIBILITY_CONSTRAINT_TYPE}
              sx={{ width: 300 }}
              renderInput={(params) => <TextField {...params} label="Basis" />}
              onChange={(e, v) => {
                setConstraints(
                  constraints.map((it) =>
                    it.tempId == constraint.tempId ? { ...it, basis: v?.value, basis_id: null } : it
                  )
                )
              }}
              size="small"
            />
            {constraint.basis == 'team' ? (
              <Autocomplete
                value={getSelectedTeam(constraint.basis_id)}
                id="autocomplete-field"
                options={teams.map((it) => ({
                  value: it.id,
                  label: it.name,
                }))}
                sx={{ width: 300 }}
                renderInput={(params) => <TextField {...params} label="Team" />}
                onChange={(e, v) => {
                  setConstraints(
                    constraints.map((it) =>
                      it.tempId == constraint.tempId
                        ? { field_template_id: field.id, basis: 'team', basis_id: v.value }
                        : it
                    )
                  )
                }}
                size="small"
              />
            ) : (
              <Autocomplete
                value={
                  constraint.basis_id
                    ? {
                        value: constraint.basis_id,
                        label: teamTypes.find((it) => it.value == constraint.basis_id)?.label,
                      }
                    : null
                }
                id="autocomplete-field-2"
                options={teamTypes}
                sx={{ width: 300 }}
                renderInput={(params) => <TextField {...params} label="Team Type" />}
                onChange={(e, v) => {
                  setConstraints(
                    constraints.map((it) =>
                      it.tempId == constraint.tempId
                        ? { field_template_id: field.id, basis: 'team_type', basis_id: v.value }
                        : it
                    )
                  )
                }}
                size="small"
              />
            )}
            <IconButton onClick={() => onDelete(constraint.tempId)}>
              <DeleteIcon size="small" />
            </IconButton>
          </div>
        )
      })}
      <div
        style={{
          display: 'flex',
          columnGap: 16,
          width: '100%',
          justifyContent: 'flex-end',
          marginTop: 16,
        }}
      >
        <Button variant="contained" onClick={addRow}>
          Add
        </Button>
        <Button color="primary" variant="contained" onClick={saveChanges}>
          Save Changes
        </Button>
      </div>
    </Box>
  )
}

export default FieldVisibilityProtectionUI
